import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import ContactLocalizationPanel from './ContactLocalizationContainer';
import { LocalizationChangeHandler } from './LocalizationContainer';
import { TargetNode } from '../../models/TargetNode';


type ChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => void;

interface TopicButtonsPanelOwnProps {
  node: TargetNode;
  // Whether the topic buttons are switched on (extra.c.c78).
  enabled: boolean;
  // The ids present in extra.k — a field is ticked iff its redisid is in here.
  extraKIds: Set<string>;
  onToggleEnabled: ChangeHandler;
  // Presence-toggles the field's redisid in extra.k.
  onChangeExtra: ChangeHandler;
  localizationChanged: LocalizationChangeHandler;
}

type FieldProps = Pick<TopicButtonsPanelOwnProps,
  'node' | 'extraKIds' | 'onChangeExtra' | 'localizationChanged'> & {
  name: string;
  redisid: string;
  label: string;
  addLocalisation: string;
};

// One labelled checkbox plus its localization panel. In the container this was
// written out 10 times verbatim.
const Field = ({
  name, redisid, label, addLocalisation,
  node, extraKIds, onChangeExtra, localizationChanged,
}: FieldProps) => (
  <>
    <label>{label}</label>
    <input type="checkbox" name={name} data-redisid={redisid}
      checked={extraKIds.has(redisid)}
      onChange={onChangeExtra}
    />
    <ContactLocalizationPanel
      node={node}
      localizationChanged={localizationChanged}
      header={addLocalisation}
      localizationIndex={redisid} />
  </>
);

// The eight buttons render two per row; title and subtitle sit above them.
const BUTTON_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8];

const TopicButtonsPanel = ({
  node, enabled, extraKIds, onToggleEnabled, onChangeExtra, localizationChanged, t,
}: TopicButtonsPanelOwnProps & WithTranslation) => {
  const addLocalisation = t('target.editor.button.addLocalisation');
  const fieldProps = { node, extraKIds, onChangeExtra, localizationChanged, addLocalisation };

  return (
    <fieldset>
      <legend>{t('target.editor.label.topicButtons')}</legend>
      <label>{t('target.editor.label.enable')}</label>
      <input type="checkbox" data-redisid="enabletopicbuttons"
        title={t('target.editor.label.topicButtons')}
        checked={enabled}
        onChange={onToggleEnabled}
      />
      <div style={{ height: "0.4em" }}></div>
      <div className={!enabled ? "hidden" : ""}>
        <div>
          <div>
            <Field {...fieldProps}
              name="topicButtonsShowTitle" redisid="topicButtonsTitle"
              label={t('target.editor.label.topicButtonsTitle')} />
          </div>
          <div>
            <Field {...fieldProps}
              name="topicButtonsShowSubtitle" redisid="topicButtonsSubtitle"
              label={t('target.editor.label.topicButtonsSubtitle')} />
          </div>
        </div>
        {[0, 2, 4, 6].map(offset => (
          <div key={offset} className="pure-u-24-24" style={{ display: "flex" }}>
            <div className="pure-u-12-24">
              <Field {...fieldProps}
                name={`topicButtonsShow_${BUTTON_NUMBERS[offset]}`}
                redisid={`topicButton_${BUTTON_NUMBERS[offset]}`}
                label={t(`target.editor.label.button${BUTTON_NUMBERS[offset]}`)} />
            </div>
            <div style={{ width: "0.4em" }}></div>
            <div className="pure-u-12-24">
              <Field {...fieldProps}
                name={`topicButtonsShow_${BUTTON_NUMBERS[offset + 1]}`}
                redisid={`topicButton_${BUTTON_NUMBERS[offset + 1]}`}
                label={t(`target.editor.label.button${BUTTON_NUMBERS[offset + 1]}`)} />
            </div>
          </div>
        ))}
      </div>
    </fieldset>
  );
};

export default withTranslation('common')(TopicButtonsPanel);
